/**
 * An appellate opinion in the sentencing workspace.
 *
 *   OpinionLinks   the cite plus the two ways to read the opinion (the court's
 *                  PDF, CourtListener) — every opinion-derived row carries it
 *   OpinionDetail  drill-down: the sentence as the opinion states it, each
 *                  value with the ¶ it comes from; the matched DOC record
 *                  beside it; key facts; sentencing issues and holdings
 *
 * The opinion and the DOC record are shown side by side and never reconciled:
 * the opinion reports the sentence as imposed, DOC the sentence now in force.
 *
 * Text we wrote from an opinion (offense labels, key facts, holdings) arrives
 * empty until the opinion has passed its privacy check; `text_withheld` says
 * so. Figures, statute, links and the Court's own paragraphs always arrive.
 *
 * Data: GET /sentencing/opinions/:id
 */
import { useEffect, useState } from 'react';
import { api } from '../api';
import {
  COURT_WORDS_NOTE, PLACE_LABELS, SECTION_FROM_NAME_NOTE, STAGE_LABELS, afterAppealLabel, formatMonths, formatTerm,
} from '../domain/sentencing';

export function OpinionLinks({ opinion, compact = false }) {
  if (!opinion) return null;
  const stop = (e) => e.stopPropagation();
  return (
    <span className="so-links">
      <span className="so-cite">{opinion.cite}</span>
      {opinion.court_pdf_url && (
        <a href={opinion.court_pdf_url} target="_blank" rel="noopener noreferrer" onClick={stop} title="The court's PDF of the opinion">
          {compact ? 'PDF' : 'Court PDF'}
        </a>
      )}
      {opinion.courtlistener_url && (
        <a href={opinion.courtlistener_url} target="_blank" rel="noopener noreferrer" onClick={stop} title="The opinion on CourtListener">
          {compact ? 'CL' : 'CourtListener'}
        </a>
      )}
    </span>
  );
}

function Pinpoints({ numbers, paragraphs, onShow, shown }) {
  if (!numbers || numbers.length === 0) return null;
  const known = new Set((paragraphs || []).map((p) => p.n));
  return (
    <span className="so-pins">
      {numbers.filter((n) => known.has(n)).map((n) => (
        <button
          key={n}
          className={`so-pin${shown === n ? ' so-pin-on' : ''}`}
          onClick={() => onShow(shown === n ? null : n)}
          title="Show this paragraph of the opinion"
        >
          ¶{n}
        </button>
      ))}
    </span>
  );
}

function amounts(s) {
  const parts = [];
  if (s.term_months != null) parts.push(formatMonths(s.term_months));
  else if (s.commitment === 'life' || s.commitment === 'death') parts.push(PLACE_LABELS[s.commitment]);
  if (s.suspended_months != null) parts.push(`${formatMonths(s.suspended_months)} suspended`);
  if (s.deferred_months != null) parts.push(`${formatMonths(s.deferred_months)} deferred`);
  if (s.parole_restriction_months != null) parts.push(`parole restriction ${formatMonths(s.parole_restriction_months)}`);
  if (s.fine_usd != null) parts.push(`fine $${Number(s.fine_usd).toLocaleString()}`);
  if (s.restitution_usd != null) parts.push(`restitution $${Number(s.restitution_usd).toLocaleString()}`);
  if (s.credit_days != null) parts.push(`${s.credit_days} days credit`);
  return parts.length ? parts.join(' · ') : 'No amount stated';
}

const BASIS_NOTE = {
  plea_agreement_term: 'plea agreement term, not imposed',
  recommended: 'recommended, not imposed',
  conviction_only: 'conviction stated, no sentence stated',
};

function OpinionSentence({ sentencings, sentences, paragraphs, textWithheld }) {
  const [shown, setShown] = useState(null);
  const para = (paragraphs || []).find((p) => p.n === shown);
  return (
    <div className="so-col">
      <div className="sb-detail-label">Sentence as the opinion states it</div>
      {sentencings.map((sg) => {
        const rows = sentences.filter((s) => s.sentencing_id === sg.id);
        if (rows.length === 0) return null;
        return (
          <div key={sg.id} className="so-stage">
            <div className="so-stage-head">
              <strong>{STAGE_LABELS[sg.stage] || sg.stage}</strong>
              {sg.imposed_on && <span>pronounced {sg.imposed_on}</span>}
              {sg.judge_name && <span>Judge {sg.judge_name}</span>}
              {sg.docket && <span className="sb-cell-mono">{sg.docket}</span>}
            </div>
            {rows.map((s) => (
              <div key={s.id} className={`so-row${s.basis !== 'imposed' ? ' so-row-context' : ''}`}>
                <span className="so-row-offense">
                  {s.mca_section && (
                    <span
                      className={`sb-mca sb-mca-other${s.mca_from_name ? ' sb-mca-from-name' : ''}`}
                      title={s.mca_from_name ? SECTION_FROM_NAME_NOTE : undefined}
                    >
                      {s.mca_section}
                    </span>
                  )}{' '}
                  {s.offense || (s.is_total ? 'Total' : textWithheld ? <em className="so-muted">offense label withheld</em> : '—')}
                </span>
                <span className="so-row-amounts">
                  {s.basis === 'imposed' ? amounts(s) : <em className="so-muted">{BASIS_NOTE[s.basis] || s.basis}</em>}
                  {s.basis === 'imposed' && s.commitment && s.commitment !== 'unknown' && s.commitment !== 'life' && s.commitment !== 'death'
                    && ` · ${PLACE_LABELS[s.commitment] || s.commitment}`}
                  {s.basis === 'imposed' && s.concurrent_or_consecutive && s.concurrent_or_consecutive !== 'unstated'
                    && ` · ${s.concurrent_or_consecutive}`}
                  {(s.designations || []).map((d) => <span key={d} className="so-tag">{d}</span>)}
                </span>
                <span className="so-row-fate">
                  {s.basis === 'imposed' && (
                    <span className={`so-fate so-fate-${s.after_appeal}`}>{afterAppealLabel(s.after_appeal)}</span>
                  )}
                  <Pinpoints numbers={s.paragraphs_resolved} paragraphs={paragraphs} onShow={setShown} shown={shown} />
                </span>
              </div>
            ))}
            {sg.total_term && (
              <div className="sb-detail-total">
                Total term: {formatTerm(sg.total_term)}
                {sg.total_term_basis === 'stated' ? ' (stated by the opinion)' : ''}
              </div>
            )}
          </div>
        );
      })}
      {para && (
        <blockquote className="so-para">
          <span className="so-para-source">{COURT_WORDS_NOTE}</span>
          <span className="so-para-n">¶{para.n}</span> {para.text}
        </blockquote>
      )}
    </div>
  );
}

function DocSide({ orders, match }) {
  if (!orders || orders.length === 0) {
    return (
      <div className="so-col">
        <div className="sb-detail-label">DOC record</div>
        <div className="so-muted">
          {match?.status === 'review'
            ? 'A possible DOC match is waiting for review.'
            : 'The DOC data holds no record of this sentence.'}
        </div>
      </div>
    );
  }
  return (
    <div className="so-col">
      <div className="sb-detail-label">
        DOC record{match?.decided_by_kind === 'person' ? ' (match confirmed by review)' : ''}
        {match?.decided_by_kind === 'model' ? ' (match made by a model reading both records; not yet checked by a person)' : ''}
      </div>
      {orders.map((o) => (
        <div key={o.id} className="so-stage">
          <div className="so-stage-head">
            <strong>{o.procedural_sentence_type || 'Sentence'}</strong>
            {o.imposed_on && <span>pronounced {o.imposed_on}</span>}
            {o.judge && <span>Judge {o.judge}</span>}
            <span className="sb-cell-mono">{o.docket}</span>
          </div>
          {(o.counts || []).map((c) => (
            <div key={c.charge_index} className="so-row">
              <span className="so-row-offense">
                {c.mca_section && <span className="sb-mca sb-mca-other">{c.mca_section}</span>} {c.description || '—'}
              </span>
              <span className="so-row-amounts">{formatTerm(c.term)}</span>
              <span className="so-row-fate" />
            </div>
          ))}
          <div className="sb-detail-total">
            Total term: {formatTerm(o.total_term)}
            {o.agg_sentence?.commit_type ? ` · ${o.agg_sentence.commit_type}` : ''}
            {o.agg_sentence?.suspension ? ` · ${o.agg_sentence.suspension}` : ''}
          </div>
        </div>
      ))}
    </div>
  );
}

const FACT_FIELDS = [
  ['conduct', 'Conduct before the sentencing court'],
  ['criminal_history', 'Criminal history'],
  ['plea_agreement', 'Plea agreement'],
  ['state_recommendation', 'State recommended'],
  ['defense_recommendation', 'Defense recommended'],
  ['psi_recommendation', 'Presentence report recommended'],
  ['sentencing_court_reasons', "Sentencing court's reasons"],
  ['victim_impact', 'Victim impact'],
  ['later_events', 'After sentencing'],
];

/**
 * `showDocSide` false: the opinion's sections without the DOC record beside them, for the
 * record page of a DOC row that already shows DOC's own figures above.
 */
export function OpinionDetail({ opinionId, showDocSide = true }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  // The key-facts paragraph on show: which narrative's pointer was clicked, and which ¶.
  const [shownFact, setShownFact] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setError(null);
    setShownFact(null);
    api.get(`/sentencing/opinions/${opinionId}`)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch((e) => { if (!cancelled) setError(e.message || 'Failed to load the opinion'); });
    return () => { cancelled = true; };
  }, [opinionId]);

  if (error) return <div className="sb-detail sb-error">{error}</div>;
  if (!detail) return <div className="sb-detail sb-detail-loading">Loading opinion…</div>;

  const facts = detail.key_facts || {};
  const shownFacts = FACT_FIELDS.filter(([k]) => facts[k]);
  const factParagraphs = facts.field_paragraphs || {};
  const hasFactPointers = shownFacts.some(([k]) => (factParagraphs[k] || []).length > 0);
  const factPara = shownFact && (detail.paragraphs || []).find((p) => p.n === shownFact.n);
  const findings = facts.court_findings || [];
  const issues = (detail.issues || []).filter((i) => i.holding);
  const defendant = (detail.defendants || [])[0];

  return (
    <div className="sb-detail so-detail">
      <div className="sb-detail-case">
        <span><strong>{detail.case_name || detail.caption_defendant || 'Opinion'}</strong></span>
        <OpinionLinks opinion={detail} />
        {detail.filed_date && <span>Decided {detail.filed_date}</span>}
        {detail.disposition && <span>Disposition: {detail.disposition.replaceAll('_', ' ')}</span>}
        {!detail.citable && <span className="so-tag">{detail.kind === 'srd_decision' ? 'Sentence Review decision' : 'Noncitable'}</span>}
      </div>
      {defendant?.sentence_verbatim && <blockquote className="so-para">{defendant.sentence_verbatim}</blockquote>}

      <div className={showDocSide ? 'so-sides' : 'so-sides so-sides-alone'}>
        <OpinionSentence
          sentencings={detail.sentencings || []}
          sentences={detail.sentences || []}
          paragraphs={detail.paragraphs || []}
          textWithheld={Boolean(detail.text_withheld)}
        />
        {showDocSide && <DocSide orders={detail.doc_orders} match={detail.doc_match} />}
      </div>
      {(detail.doc_orders || []).length > 0 && (
        <div className="so-muted so-sides-note">
          Shown side by side, not reconciled: the opinion reports the sentence as imposed, DOC the sentence now in
          force (after any amendment, revocation or sentence review). Check the judgment.
        </div>
      )}

      {detail.text_withheld && (
        <div className="so-muted so-withheld">Key facts, holdings and offense labels: {detail.text_withheld.toLowerCase()}.</div>
      )}

      {shownFacts.length > 0 && (
        <>
          <div className="sb-detail-label">
            Key facts, as the opinion recites them
            {hasFactPointers && (
              <span className="so-muted so-fact-note" title="Found by matching our wording to the opinion's paragraphs. A fact can rest on more paragraphs than the ones marked.">
                {' '}· ¶ = where to start reading
              </span>
            )}
          </div>
          {shownFacts.map(([k, label]) => (
            <div key={k}>
              <div className="so-fact">
                <span className="so-fact-key">{label}</span>
                <span>
                  {facts[k]}{' '}
                  <Pinpoints
                    numbers={factParagraphs[k]}
                    paragraphs={detail.paragraphs || []}
                    shown={shownFact?.field === k ? shownFact.n : null}
                    onShow={(n) => setShownFact(n == null ? null : { field: k, n })}
                  />
                </span>
              </div>
              {factPara && shownFact.field === k && (
                <blockquote className="so-para so-fact-para">
                  <span className="so-para-source">{COURT_WORDS_NOTE}</span>
                  <span className="so-para-n">¶{factPara.n}</span> {factPara.text}
                </blockquote>
              )}
            </div>
          ))}
        </>
      )}
      {findings.length > 0 && (
        <>
          <div className="sb-detail-label">Express findings of the sentencing court</div>
          {findings.map((f, i) => (
            <div key={`f-${i}`} className="so-fact">
              <span className="so-fact-key">{f.kind === 'aggravating' ? 'Aggravating circumstance' : 'Mitigating circumstance'}</span>
              <span>{f.finding}{f.statute ? ` (§ ${f.statute}, MCA)` : ''}</span>
            </div>
          ))}
        </>
      )}
      {issues.length > 0 && (
        <>
          <div className="sb-detail-label">Sentencing issues decided</div>
          {issues.map((i) => (
            <div key={i.id} className="so-issue">
              <div className="so-issue-head">
                <strong>{i.issue_label || i.issue_tag}</strong>
                {i.outcome && <span className="so-tag">{i.outcome.replaceAll('_', ' ')}</span>}
                {i.para_first != null && (
                  <span className="so-muted">
                    {detail.cite}, {i.para_first === i.para_last ? `¶ ${i.para_first}` : `¶¶ ${i.para_first}–${i.para_last}`}
                  </span>
                )}
              </div>
              <div>{i.holding}</div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
